/**
 * Teto de seleção do relatório único de lotes (ZC-46).
 *
 * Mora num mixin, e não copiado nas duas telas, porque Viagem e Diversos têm a MESMA regra e o
 * mesmo botão; duas cópias divergem na primeira vez que um número muda.
 *
 * Por que o teto é por ANEXO e **só** por anexo: o tempo de emissão é linear no número de anexos,
 * não no de lotes. E lote não limita anexo -- a pior janela de 5 lotes da base tem **342 anexos**
 * (LUCIO MAURO MARTINS VIANA), o que tornava um teto de lote enganoso: parecia limitar e não
 * limitava.
 *
 * O teto de lote existiu por um tempo como trava de sanidade e o Rodrigo mandou tirar. Medido antes
 * de tirar, o pior caso com **só** os 105 anexos (lotes do mais leve pro mais pesado, acumulando):
 * **34 lotes no Diversos e 54 no Viagem**, com média de 9 e 8. Cabe: 54 lotes dão 2 folhas de
 * índice (42 por folha) e o custo segue sendo dos anexos.
 *
 * ## Três furos que a primeira versão tinha, achados por crítico adversarial
 *
 * 1. **A soma vinha de `details`**, que é só o recorte atual. Marcar um lote, filtrar por outro
 *    número de lote e marcar de novo zerava a soma e deixava passar o dobro do teto. Hoje a soma
 *    vem do `anexosPorLote`, mapa acumulado por `loteID` que sobrevive à troca de `details`.
 * 2. **`motivoTetoRelatorio` devolvia `null` para qualquer seleção não-vazia**, então nem o
 *    `:disabled` do botão nem o guard do handler barravam excesso: o único ponto de imposição era
 *    o `podeMarcarLote`, desarmado pelo furo 1. Hoje o motivo checa os dois tetos também no modo
 *    de seleção explícita.
 * 3. **"Selecionar todos" com filtro ativo** media o teto em 1 linha e mandava `LoteIds: []`, que
 *    o backend lê como TODOS — liberava um relatório de 39 lotes. Hoje o modo todos exige filtro
 *    desligado (`filtroDeLoteAtivo`), porque só sem filtro `details` é o conjunto inteiro.
 *
 * ## Escape de um lote só
 *
 * O teto de anexo vale **do segundo lote em diante**: um lote sozinho sempre pode ser baixado.
 * Sem isso, lote maior que o teto ficaria impossível de baixar para sempre — e existe: medido em
 * produção, **10 pares funcionário/lote de Viagem passam de 105 anexos, o maior com 184** (no
 * Diversos são 0, o maior tem 103). Quem guarda esse caso é o teto do servidor (250 no Viagem).
 *
 * A tela espera do componente: `details` (linhas da grid, com `anexos` e `loteID`),
 * `selecionados`, `todosOsLotes` e `collabId`; e deve chamar `registrarAnexosDeDetalhes()` depois
 * de cada carga da grid e manter `filtroDeLoteAtivo`.
 */
const TETO_ANEXOS = 105 // ~60 s de emissão no Diversos, que é o módulo de anexo pesado

export default {
  data () {
    return {
      tetoAnexos: TETO_ANEXOS,
      // loteID -> anexos, acumulado a cada carga da grid. NÃO derivar de `details`: ele é
      // substituído pelo filtro por número de lote, e a soma de uma seleção antiga cairia a 0.
      anexosPorLote: {},
      // o filtro por número de lote deixa `details` com um recorte. Enquanto estiver ligado,
      // `details.length` não representa o total do colaborador.
      filtroDeLoteAtivo: false,
      // loteIDs já baixados nesta sessão. Só SINALIZA — não impede baixar de novo (pedido
      // explícito do Rodrigo). Vive em sessionStorage porque a tela é recriada a cada volta
      // do Retornar e perder a marca a cada navegação a tornaria inútil.
      baixados: []
    }
  },

  computed: {
    // soma de anexos das linhas marcadas, lida do mapa acumulado. Lote sem contagem conhecida
    // (back antigo, ou a contagem falhou e o controller logou) entra como 0 — nesse caso quem
    // trava é o teto de lote.
    anexosSelecionados () {
      return this.selecionados.reduce((soma, id) => soma + (Number(this.anexosPorLote[id]) || 0), 0)
    },

    anexosTotais () {
      if (!Array.isArray(this.details)) return 0
      return this.details.reduce((soma, d) => soma + (Number(d.anexos) || 0), 0)
    },

    // motivo pelo qual o botão está travado, ou null. É o texto do tooltip: travar sem dizer por
    // quê é o mesmo defeito do truncamento silencioso da task 026, só do outro lado.
    motivoTetoRelatorio () {
      if (this.todosOsLotes) {
        if (this.filtroDeLoteAtivo) {
          return 'O modo "todos os lotes" ignora o filtro e gera TODOS os lotes do colaborador. Limpe o filtro para usá-lo, ou marque os lotes na primeira coluna.'
        }
        if (!Array.isArray(this.details) || this.details.length === 0) return 'Nenhum lote nesta tela.'
        if (this.anexosTotais > TETO_ANEXOS) {
          return `Os ${this.details.length} lotes somam ${this.anexosTotais} anexos e o máximo por relatório é ${TETO_ANEXOS}. Marque menos lotes.`
        }
        return null
      }
      if (this.selecionados.length === 0) return 'Marque os lotes na primeira coluna.'
      // o teto vale aqui também. Sem isto o botão libera qualquer seleção e o `podeMarcarLote`
      // fica sendo a única barreira -- foi exatamente esse o furo 2.
      // E vale do SEGUNDO lote em diante: um lote sozinho sempre pode ser baixado, senão lote
      // maior que o teto fica inalcançável (10 pares de Viagem, maior com 184 anexos).
      if (this.selecionados.length > 1 && this.anexosSelecionados > TETO_ANEXOS) {
        return `Os lotes marcados somam ${this.anexosSelecionados} anexos e o máximo por relatório é ${TETO_ANEXOS}. Desmarque alguns.`
      }
      return null
    }
  },

  methods: {
    /** Acumula a contagem de anexo das linhas carregadas. Chamar após cada carga da grid. */
    registrarAnexosDeDetalhes () {
      if (!Array.isArray(this.details)) return
      const mapa = Object.assign({}, this.anexosPorLote)
      this.details.forEach(d => {
        if (d && d.loteID) mapa[d.loteID] = Number(d.anexos) || 0
      })
      this.anexosPorLote = mapa // troca o objeto: Vue 2 não reage a chave nova sem $set
    },

    /**
     * Pode marcar esta linha? Linha já marcada sempre pode (para poder DESmarcar).
     * O que fecha é qualquer um dos dois tetos.
     */
    podeMarcarLote (detail) {
      if (!detail) return false
      if (this.selecionados.includes(detail.loteID)) return true
      // ESCAPE: um lote SOZINHO sempre pode, mesmo estourando o teto de anexo. Sem isto, lote
      // maior que o teto fica impossivel de baixar para sempre -- e existe: medido em producao,
      // 10 pares funcionario/lote de Viagem passam de 105 anexos, o maior com 184 (no Diversos
      // sao 0, maior 103). Quem continua guardando esse caso e o teto do servidor (250 no
      // Viagem), e a 0,21 s/anexo medido la 184 anexos saem em ~39 s.
      if (this.selecionados.length === 0) return true
      return this.anexosSelecionados + (Number(detail.anexos) || 0) <= TETO_ANEXOS
    },

    /** Tooltip da linha travada — mesma razão do tooltip do botão. */
    motivoLoteTravado (detail) {
      if (!detail || this.podeMarcarLote(detail)) return ''
      const somaria = this.anexosSelecionados + (Number(detail.anexos) || 0)
      return `Este lote tem ${Number(detail.anexos) || 0} anexos e somaria ${somaria}; o máximo por relatório é ${TETO_ANEXOS} (~60 s de emissão). Desmarque os outros para baixar este sozinho.`
    },

    foiBaixado (loteID) {
      return this.baixados.includes(loteID)
    },

    chaveDosBaixados () {
      return 'lotesBaixados:' + this.$route.path + ':' + (this.collabId || '')
    },

    carregarBaixados () {
      try {
        const salvo = sessionStorage.getItem(this.chaveDosBaixados())
        this.baixados = salvo ? JSON.parse(salvo) : []
        if (!Array.isArray(this.baixados)) this.baixados = []
      } catch (e) { this.baixados = [] } // navegador sem storage ou JSON corrompido: segue vazio
    },

    /**
     * Marca como baixados. Chamado DEPOIS do download dar certo — marcar antes mentiria em cima
     * de um 413 ou de um timeout.
     */
    registrarBaixados (loteIds) {
      const novos = (loteIds && loteIds.length)
        ? loteIds
        : (Array.isArray(this.details) ? this.details.map(d => d.loteID) : [])
      novos.forEach(id => { if (id && !this.baixados.includes(id)) this.baixados.push(id) })
      try {
        sessionStorage.setItem(this.chaveDosBaixados(), JSON.stringify(this.baixados))
      } catch (e) { /* idem: a marca fica só em memória */ }
    }
  }
}



// WEBPACK FOOTER //
// ./src/shared/mixins/tetoRelatorioLotes.js